import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import JoditEditor, { Jodit } from "jodit-react";
import { parseCommand, stmjsonClick } from "../MainPage/NotWorkArea(Side&Head)/Header/Header";
import smetaAxios from "../SmetaAxios";
import { Prefixes } from "../MainPage/Tools/ProjectX";
import { renderMessageBox } from "../TokenProcessing/TokenProcessing";
import { appConfig, smetaStore } from "../../atoms";
import { getStrDateOfDateObj, getStrTimeOfDateObj } from "../MainPage/Tools/Tools";
import { htmlToMime } from "./DialogHtml";
import "./TextEditor.scss";

const onEditorClick = (e) => {
  if (!e.target) return;
  const target = e.target.closest("a");
  if (!target) return;
  e.preventDefault();
  const command = parseCommand(target.href);
  if (command.command) stmjsonClick(command);
  else if (target.href.includes("mailto")) {
    window.location.href = target.href.match(/mailto:[^]*/)?.[0];
  } else window.open(target.href);
};

export const confirmLoadImg = (espk) => {
  return new Promise((resolve) => {
    if (!espk) return resolve(false);
    renderMessageBox({
      dlgType: 3,
      message: "Вставить изображение как ссылку?",
      buttons: 12,
      closeF: (e) => {
        console.log(e);
      },
      selectF: (event) => {
        const {
          target: { value },
        } = event;
        resolve(value === "6");
      },
    });
  });
};

const loadImgLink = ({ base64, docId, fileName }) => {
  if (!base64.includes("base64")) return;
  const config = {
    prefix: Prefixes.progs,
    loadMask: true,
    params: {
      name: "GetFileLinkParams",
      binary: "1",
    },
  };
  return smetaAxios.post("ExecABLProgram", { DocID: docId, $content: fileName, rcdata: base64.split(",")[1] }, config);
};

const keyPass = [
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F2",
  "CapsLock",
  "Shift",
  "Alt",
  "Meta",
  "Tab",
  "Control",
  "Insert",
  "Pause",
  "ScrollLock",
  "NumLock",
  "PageUp",
  "PageUpDown",
  "Escape",
  "End",
  "Home",
  "ContextMenu",
];

const TextEditor = ({
  inputValue,
  setValue,
  readonly,
  height,
  setInputValue,
  onPostRecord,
  docId,
  onSave,
  linksEnabled,
}) => {
  const [defaultValue, setDefaultValue] = useState(inputValue.replaceAll("<a ", '<a style="cursor:pointer;" '));
  const {
    smeta: { espk },
  } = smetaStore.get(appConfig);
  const boolRef = useRef(false);
  const editor = useRef(null);
  const div = useRef(null);

  const preparePaste = (jodit) => {
    jodit.events.on(
      "beforePaste",
      (e) => {
        e.preventDefault();
        const dataTransfer = Jodit.modules.Helpers.getDataTransfer(e);

        const pastedHTML = dataTransfer.getData(Jodit.constants.TEXT_HTML);
        const pastedText = dataTransfer.getData(Jodit.constants.TEXT_PLAIN);
        const pastedFiles = dataTransfer.files;
        if (pastedFiles.length > 0) {
          confirmLoadImg(Boolean(espk) && linksEnabled).then((needLink) => {
            // image only
            Array.from(pastedFiles).forEach((file) => {
              if (file.type.startsWith("image/")) {
                const reader = new FileReader();

                reader.onload = (event) => {
                  let content = `<img src="${event.target.result}" alt="${file.name}" style="line-height: 10px;" />`;
                  if (!needLink) return jodit.s.insertHTML(content);
                  const fileName = `Рисунок${getStrDateOfDateObj()}${getStrTimeOfDateObj()}.jpg`;
                  loadImgLink({ base64: event.target.result, docId, fileName }).then((res) => {
                    const hrefCommand = `stmjson:module,command=request,module=programs,token=ExecABLProgram,params:{${res.content}}`;
                    content =
                      `<a href='${hrefCommand}' style='cursor:pointer; color:blue; text-decoration:underline;'>` +
                      fileName +
                      "<a/>";
                    jodit.s.insertHTML(content);
                    setTimeout(() => {
                      onPostRecord(htmlToMime(jodit.value), jodit.text);
                    }, 500);
                  });
                };

                reader.readAsDataURL(file);
              }
            });
            return false;
          });
        } else if (pastedHTML || pastedText) {
          jodit.s.insertHTML(pastedHTML || pastedText);
        }

        return false;
      },
      { top: true },
    );
  };

  useLayoutEffect(() => {
    Jodit.plugins.add("preparePaste", preparePaste);
  }, []);

  useEffect(() => {
    fullHeightAuto();
  }, [editor.current]);

  useEffect(() => {
    setDefaultValue(inputValue.replaceAll("<a ", '<a style="cursor:pointer;" '));
  }, [inputValue]);

  const config = useMemo(
    () => ({
      height: "auto",
      minHeight: 0,
      readonly: readonly ? readonly : false,
      addNewLine: false,
      iframe: true,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      allowTabNavigation: true,
      extraPlugins: ["preparePaste"],
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      events: {
        click: onEditorClick,
        change: (newHtmlContent) => {
          if (boolRef.current) {
            boolRef.current = false;
            const textContent = getTextContent();
            setInputValue(newHtmlContent, textContent);
          }
        },
        keydown: (e) => {
          if (keyPass.includes(e.code)) return;
          if (e.ctrlKey && e.code === "KeyS") {
            e.preventDefault();
            return onSave && onSave(e);
          }
          boolRef.current = true;
        },
        paste: () => {
          boolRef.current = true;
        },
        drop: () => {
          boolRef.current = true;
        },
        beforeCommand: () => {
          boolRef.current = true;
        },
      },
      // extraPlugins: ["preparePaste"],
      allowResizeX: false,
      allowResizeY: false,
      toolbarButtonSize: "small",
      editHTMLDocumentMode: false,
      toolbarAdaptive: false,
      saveModeInStorage: false,
      saveHeightInStorage: false,
      disablePlugins: "add-new-line,about,clean-html,placeholder,class-span,inline-popup",
      buttons: "bold,italic,underline,font,fontsize,indent,outdent,left,brush,undo,redo,source,print",
      removeButtons: ["undo", "redo"],
      uploader: {
        insertImageAsBase64URI: true,
        defaultHandlerSuccess: () => {
          return false;
        },
      },
    }),
    [],
  );

  const deleteLink = () => {
    const link = document.getElementsByClassName("jodit-status-bar-link");
    if (link && link.length > 0) {
      link[0].style.display = "none";
    }
  };

  const getTextContent = () => {
    if (!editor.current) return "";
    if (div.current) {
      const iframe = div.current.querySelector("iframe");
      if (!iframe) return "";
      return iframe.contentWindow.document.body.outerText;
    }
  };

  const fullHeightAuto = (elem) => {
    const JWorkPlace = elem ? elem.parentElement : document.getElementsByClassName("jodit-workplace")[0];
    if (JWorkPlace) {
      // setTimeout(() => {
      //   JWorkPlace.style.height = `calc(100% - ${"33px"})`;
      // }, 1000);
      // JWorkPlace.classList.add("joditHeight");
      // JWorkPlace.firstChild.style.height = "100%";
      if (!elem) {
        JWorkPlace.firstChild.onfocus = function (e) {
          fullHeightAuto(e.target);
          deleteLink();
        };
      }
    }
  };

  return (
    <div className="smeta-text-editor" style={{ height: `calc(100% - ${height})` }} ref={div}>
      <JoditEditor
        ref={editor}
        value={defaultValue}
        config={config}
        tabIndex={1} // tabIndex of textarea
        onBlur={(newHtmlContent, e) => {
          try {
            if (e.relatedTarget.className.includes("CloseButtonForGrid")) return;
            const textContent = getTextContent();
            fullHeightAuto();
            setValue(newHtmlContent, textContent);
            // setEditorValue(newContent);
          } catch {
            console.error("Потеря фокуса");
            editor.current && editor.current.focus();
          }
        }}
      />
    </div>
  );
};

export default TextEditor;
